import React from 'react'
import { refDataApi, type RefDataItem } from '../api/refDataApi'

type RefDataState = {
  items: RefDataItem[]
  loading: boolean
  error: string | null
}

const cache = new Map<string, RefDataItem[]>()
const inflight = new Map<string, Promise<RefDataItem[]>>()

async function fetchGroup(groupKey: string): Promise<RefDataItem[]> {
  const cached = cache.get(groupKey)
  if (cached) return cached

  const inFlight = inflight.get(groupKey)
  if (inFlight) return inFlight

  const p = refDataApi
    .listByGroup(groupKey)
    .then((items) => {
      const safe = items ?? []
      cache.set(groupKey, safe)
      return safe
    })
    .finally(() => {
      inflight.delete(groupKey)
    })

  inflight.set(groupKey, p)
  return p
}

export function useRefDataGroups(groupKeys: string[]) {
  const keys = React.useMemo(() => Array.from(new Set(groupKeys)).filter(Boolean), [groupKeys])
  const [state, setState] = React.useState<Record<string, RefDataState>>(() => {
    const initial: Record<string, RefDataState> = {}
    keys.forEach((k) => {
      initial[k] = { items: cache.get(k) ?? [], loading: !cache.has(k), error: null }
    })
    return initial
  })

  React.useEffect(() => {
    let cancelled = false

    keys.forEach((k) => {
      if (cache.has(k)) {
        setState((s) => ({ ...s, [k]: { items: cache.get(k) ?? [], loading: false, error: null } }))
        return
      }

      setState((s) => ({ ...s, [k]: { items: s[k]?.items ?? [], loading: true, error: null } }))
      void fetchGroup(k)
        .then((items) => {
          if (cancelled) return
          setState((s) => ({ ...s, [k]: { items, loading: false, error: null } }))
        })
        .catch((e: any) => {
          if (cancelled) return
          setState((s) => ({ ...s, [k]: { items: s[k]?.items ?? [], loading: false, error: e?.message ?? 'Veriler alınamadı' } }))
        })
    })

    return () => {
      cancelled = true
    }
  }, [keys])

  return state
}

